// PublicEventQuickAdd — the EVENT PRODUCER. Dan orates a brief (dictation)
// plus bare logistics; the produceEventListing callable (Claude + web
// search) researches the event and drafts the full public listing — punchy
// copy at every length and a curated link set (≤4: ticketing → headliner →
// venue → event social), each with the source page it was found on. Dan
// reviews and edits the draft, then one Publish tap runs publishEventListing:
// booking doc (website updates instantly) + clean public-GCal event
// (publicCalendarEventId stamped so the sync adopts it) + resolves the
// originating loose-ends publish tug.
//
// Works in two auth modes, mirroring /fix: staff login (navigate present) or
// the ?k= loose-ends link key (opened from a GCal tug in Safari, navigate
// null). All writes go through callables, which enforce auth server-side.

import { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { STAFF_THEME, FONT } from "../../theme/staff";
import { useDictation, DictateButton } from "../../hooks/useDictation";
import AddressAutocomplete from "../../components/AddressAutocomplete";

const BRAND = { ...STAFF_THEME, bg: STAFF_THEME.dark, line: STAFF_THEME.raised };

const LINK_TYPE_BADGE = {
  tickets: "🎟", artist: "🎤", venue: "📍", social: "📣", other: "🔗",
};

export default function PublicEventQuickAdd({ navigate }) {
  const params = new URLSearchParams(window.location.search);
  const linkKey = params.get("k") || null;

  // Prefills — the fixer's publish tug hands its event over via query params.
  const [eventName,    setEventName]    = useState(params.get("title") || "");
  const [date,         setDate]         = useState(params.get("date") || "");
  const [startTime,    setStartTime]    = useState(params.get("start") || "");
  const [endTime,      setEndTime]      = useState("");
  const [venueName,    setVenueName]    = useState("");
  const [venueAddress, setVenueAddress] = useState(params.get("address") || "");
  const [indoorOutdoor, setIndoorOutdoor] = useState("");
  const [ticketingUrl, setTicketingUrl] = useState("");
  const [brief,        setBrief]        = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const internalEventId = params.get("eventId") || null;
  const itemId          = params.get("item") || null;

  // The reviewable draft.
  const [listing,   setListing]   = useState(null); // {publicTitle, publicShortTitle, publicBlurb, publicDescription, ticketingUrl, publicLinks, openQuestions}
  const [producing, setProducing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null); // {bookingId}
  const [error,     setError]     = useState(null);

  const briefDictation = useDictation(brief, setBrief);
  const notesDictation = useDictation(internalNotes, setInternalNotes);

  const canProduce = (brief.trim() || eventName.trim()) && !producing;
  const canPublish = listing && (listing.publicTitle || "").trim() && date && !publishing;

  function setListingField(key, value) {
    setListing((prev) => ({ ...prev, [key]: value }));
  }

  function fieldsPayload() {
    return {
      eventName: eventName.trim() || null,
      date: date || null,
      startTime: startTime || null,
      endTime: endTime || null,
      venueName: venueName.trim() || null,
      venueAddress: venueAddress.trim() || null,
      indoorOutdoor: indoorOutdoor || null,
      ticketingUrl: ticketingUrl.trim() || null,
    };
  }

  async function produce() {
    if (!canProduce) return;
    setProducing(true);
    setError(null);
    try {
      // The producer researches on the web — give it room (default callable
      // timeout is 70s; a search-heavy run can take 2-3 minutes).
      const fn = httpsCallable(getFunctions(getApp()), "produceEventListing", { timeout: 300000 });
      const res = await fn({ brief: brief.trim(), fields: fieldsPayload(), k: linkKey || undefined });
      setListing(res.data);
      if (res.data?.ticketingUrl && !ticketingUrl.trim()) setTicketingUrl(res.data.ticketingUrl);
    } catch (e) {
      setError(e.message || "The producer run failed — try again.");
    } finally {
      setProducing(false);
    }
  }

  async function publish() {
    if (!canPublish) return;
    setPublishing(true);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "publishEventListing", { timeout: 120000 });
      const res = await fn({
        listing: { ...listing, ticketingUrl: ticketingUrl.trim() || listing.ticketingUrl || null },
        fields: fieldsPayload(),
        internalNotes: internalNotes.trim() || null,
        internalEventId: internalEventId || undefined,
        itemId: itemId || undefined,
        k: linkKey || undefined,
      });
      setPublished({ bookingId: res.data?.bookingId });
    } catch (e) {
      setError(e.message || "Publish failed.");
      setPublishing(false);
    }
  }

  function removeLink(idx) {
    setListing((prev) => ({ ...prev, publicLinks: prev.publicLinks.filter((_, i) => i !== idx) }));
  }
  function togglePrimary(idx) {
    setListing((prev) => ({
      ...prev,
      publicLinks: prev.publicLinks.map((l, i) => ({ ...l, isPrimary: i === idx ? !l.isPrimary : false })),
    }));
  }

  // ── Published! ────────────────────────────────────────────────────────────
  if (published) {
    return (
      <Shell navigate={navigate} linkKey={linkKey}>
        <div style={{ textAlign: "center", padding: "3rem 1rem", display: "flex", flexDirection: "column", gap: "1rem", alignItems: "center" }}>
          <div style={{ fontSize: "3rem" }}>🎉</div>
          <div style={{ color: BRAND.gold, fontWeight: 800, fontSize: "1.2rem" }}>Published!</div>
          <p style={{ color: BRAND.muted, fontSize: "0.9rem", maxWidth: "420px", lineHeight: 1.5, margin: 0 }}>
            The website updated instantly; subscribed calendar apps catch up within a couple hours.
          </p>
          <a href={`https://thejoyofpancakes.com/events/${published.bookingId}`}
            style={{ ...primaryBtn, textDecoration: "none", display: "inline-block" }}>
            View the listing →
          </a>
          {linkKey && !navigate && (
            <a href={`/fix?k=${linkKey}`} style={{ color: BRAND.muted, fontSize: "0.85rem" }}>← Back to loose ends</a>
          )}
          {navigate && (
            <button onClick={() => navigate("/calendar")} style={btnGhost}>← Staff calendar</button>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell navigate={navigate} linkKey={linkKey}>
      <p style={{ color: BRAND.muted, fontSize: "0.88rem", lineHeight: 1.5, margin: 0 }}>
        Tell the producer about the event — talk it out. It researches the web, drafts the copy, and hunts down the links a guest would want. You review everything before it publishes.
      </p>

      {/* Section 1 — The brief */}
      <Section
        label="The brief"
        hint="Who, what, why it's cool, ticket situation, other artists — anything worth considering."
        right={
          <DictateButton
            recording={briefDictation.recording}
            interim={briefDictation.interim}
            speechSupported={briefDictation.speechSupported}
            onStart={briefDictation.start}
            onStop={briefDictation.stop}
            error={briefDictation.error}
          />
        }
      >
        <Row>
          <Field>
            <textarea value={brief} onChange={(e) => setBrief(e.target.value)}
              placeholder={briefDictation.recording ? "Listening…" : "e.g. Pancake art at the Metal Brunch at Off Broadway, doors at 10, tickets on Eventbrite, Sista Slam is headlining, all ages…"}
              rows={4}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit", borderColor: briefDictation.recording ? "#ef537e" : BRAND.line }} />
          </Field>
        </Row>
      </Section>

      {/* Section 2 — Logistics */}
      <Section label="Logistics" hint="What you know for sure. The producer treats these as ground truth.">
        <Row>
          <Field label="Event name" hint="Internal label — what you'd call it in conversation.">
            <input type="text" value={eventName} onChange={(e) => setEventName(e.target.value)}
              placeholder="e.g. Metal Brunch @ Off Broadway" style={inputStyle} />
          </Field>
        </Row>
        <Row>
          <Field label="Date *" grow={1}>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Start" grow={1}>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="End" grow={1}>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={inputStyle} />
          </Field>
        </Row>
        <Row>
          <Field label="Venue name" grow={1}>
            <input type="text" value={venueName} onChange={(e) => setVenueName(e.target.value)}
              placeholder="Off Broadway" style={inputStyle} />
          </Field>
          <Field label="Venue address" grow={2} hint="Start typing — picks from Google Places.">
            <AddressAutocomplete
              value={venueAddress}
              onChange={setVenueAddress}
              placeholder="3509 Lemp Ave, St. Louis…"
              inputStyle={inputStyle}
            />
          </Field>
        </Row>
        <Row>
          <Field label="Indoor / outdoor" grow={1}>
            <select value={indoorOutdoor} onChange={(e) => setIndoorOutdoor(e.target.value)} style={inputStyle}>
              <option value="">—</option>
              <option value="indoor">Indoor</option>
              <option value="outdoor">Outdoor</option>
              <option value="both">Both / mixed</option>
            </select>
          </Field>
          <Field label="Ticket URL" grow={2} hint="If you have it. Otherwise the producer goes looking.">
            <input type="url" value={ticketingUrl} onChange={(e) => setTicketingUrl(e.target.value)}
              placeholder="https://…" style={inputStyle} />
          </Field>
        </Row>
      </Section>

      {/* The Produce button */}
      <button onClick={produce} disabled={!canProduce}
        style={{ ...primaryBtn, background: BRAND.blue || "#1f59db", opacity: !canProduce ? 0.5 : 1, padding: "0.8rem 1.1rem", fontSize: "1rem" }}>
        {producing ? "🎬 Producing… (researching the web, ~1–2 min)" : listing ? "🎬 Produce again" : "🎬 Produce listing"}
      </button>

      {/* Section 3 — The draft, reviewable */}
      {listing && (
        <Section label="The listing" hint="Everything below is editable. This is what publishes.">
          {listing.openQuestions?.length > 0 && (
            <div style={{ background: "rgba(252,179,31,0.1)", border: "1px solid rgba(252,179,31,0.4)", borderRadius: "10px", padding: "0.7rem 0.9rem", fontSize: "0.85rem", color: BRAND.gold }}>
              <div style={{ fontWeight: 700, marginBottom: "0.3rem" }}>The producer wants to know:</div>
              {listing.openQuestions.map((q, i) => (
                <div key={i} style={{ color: BRAND.cream, lineHeight: 1.5 }}>• {q}</div>
              ))}
            </div>
          )}
          <Row>
            <Field label="Public title" grow={2}>
              <input type="text" value={listing.publicTitle || ""} onChange={(e) => setListingField("publicTitle", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Tile title" grow={1} hint="≤14 chars, tiny calendar tiles.">
              <input type="text" maxLength={14} value={listing.publicShortTitle || ""} onChange={(e) => setListingField("publicShortTitle", e.target.value)} style={inputStyle} />
            </Field>
          </Row>
          <Row>
            <Field label="Skim line" hint="One sentence — shows on the card.">
              <input type="text" value={listing.publicBlurb || ""} onChange={(e) => setListingField("publicBlurb", e.target.value)} style={inputStyle} />
            </Field>
          </Row>
          <Row>
            <Field label="Description">
              <textarea value={listing.publicDescription || ""} onChange={(e) => setListingField("publicDescription", e.target.value)}
                rows={4} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
            </Field>
          </Row>

          {/* Link chips — tap to verify, ✕ to drop, ★ = the primary button */}
          <Field label="Links" hint="Tap a chip to open and check it's the right page. ★ marks the main button guests see.">
            <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
              {(listing.publicLinks || []).length === 0 && (
                <div style={{ color: BRAND.muted, fontSize: "0.82rem" }}>No links found — add a ticket URL above or produce again with more detail in the brief.</div>
              )}
              {(listing.publicLinks || []).map((l, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: BRAND.panel, border: `1px solid ${l.isPrimary ? BRAND.gold : BRAND.line}`, borderRadius: "10px", padding: "0.5rem 0.7rem" }}>
                  <button onClick={() => togglePrimary(i)} title="Make this the main button"
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1rem", color: l.isPrimary ? BRAND.gold : BRAND.muted, padding: 0 }}>
                    {l.isPrimary ? "★" : "☆"}
                  </button>
                  <a href={l.url} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, textDecoration: "none" }}>
                    <div style={{ color: BRAND.cream, fontSize: "0.88rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {LINK_TYPE_BADGE[l.type] || "🔗"} {l.label} ↗
                    </div>
                    <div style={{ color: BRAND.muted, fontSize: "0.72rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {l.url}{l.source ? ` · found via ${shortHost(l.source)}` : ""}
                    </div>
                  </a>
                  <button onClick={() => removeLink(i)} title="Remove this link"
                    style={{ background: "none", border: "none", cursor: "pointer", color: BRAND.muted, fontSize: "0.95rem", padding: "0 0.2rem" }}>✕</button>
                </div>
              ))}
            </div>
          </Field>
        </Section>
      )}

      {/* Section 4 — Internal notes */}
      <Section
        label="Internal notes"
        hint="For you only — never published."
        right={
          <DictateButton
            recording={notesDictation.recording}
            interim={notesDictation.interim}
            speechSupported={notesDictation.speechSupported}
            onStart={notesDictation.start}
            onStop={notesDictation.stop}
            error={notesDictation.error}
          />
        }
      >
        <Row>
          <Field>
            <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)}
              placeholder={notesDictation.recording ? "Listening…" : "Load-in at 9, ask for Marcus, bring the long extension cord."}
              rows={2}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit", borderColor: notesDictation.recording ? "#ef537e" : BRAND.line }} />
          </Field>
        </Row>
      </Section>

      {error && <div style={{ color: "#ff8a8a", fontSize: "0.85rem", padding: "0.6rem 0.85rem", background: "rgba(201,68,68,0.12)", border: "1px solid rgba(201,68,68,0.4)", borderRadius: "8px" }}>{error}</div>}

      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", alignItems: "center" }}>
        {!listing && <span style={{ color: BRAND.muted, fontSize: "0.8rem" }}>Produce a draft first — publishing is always reviewed.</span>}
        <button onClick={publish} disabled={!canPublish}
          style={{ ...primaryBtn, opacity: !canPublish ? 0.5 : 1, cursor: !canPublish ? "not-allowed" : "pointer" }}>
          {publishing ? "Publishing…" : "🚀 Publish — website + public calendar"}
        </button>
      </div>
    </Shell>
  );
}

function shortHost(s) {
  try { return new URL(s).hostname.replace(/^www\./, ""); }
  catch { return s.slice(0, 40); }
}

function Shell({ navigate, linkKey, children }) {
  return (
    <div style={{ background: BRAND.bg, minHeight: "100vh", color: BRAND.cream, fontFamily: FONT.body }}>
      <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.line}`, display: "flex", alignItems: "center", gap: "0.75rem" }}>
        {navigate
          ? <button onClick={() => navigate("/crm")} style={btnGhost}>← Bookings</button>
          : linkKey
            ? <a href={`/fix?k=${linkKey}`} style={{ ...btnGhost, textDecoration: "none" }}>← Loose ends</a>
            : null}
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>🎬 Event Producer</div>
      </div>
      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "1.25rem 1rem 3rem", display: "flex", flexDirection: "column", gap: "1.4rem" }}>
        {children}
      </div>
    </div>
  );
}

function Section({ label, hint, right, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
        <div>
          <div style={{ color: BRAND.gold, fontSize: "0.78rem", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700 }}>{label}</div>
          {hint && <div style={{ color: BRAND.muted, fontSize: "0.78rem", marginTop: "2px", lineHeight: 1.4 }}>{hint}</div>}
        </div>
        {right}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
        {children}
      </div>
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>{children}</div>;
}

function Field({ label, hint, children, grow = 1 }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: `${grow} 1 200px`, minWidth: 0 }}>
      {label && <span style={{ color: BRAND.cream, fontSize: "0.78rem", fontWeight: 600 }}>{label}</span>}
      {children}
      {hint && <span style={{ color: BRAND.muted, fontSize: "0.7rem" }}>{hint}</span>}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  background: BRAND.panel,
  border: `1px solid ${BRAND.line}`,
  borderRadius: "8px",
  color: "white",
  fontSize: "0.9rem",
  padding: "0.55rem 0.7rem",
  outline: "none",
  boxSizing: "border-box",
};

const btnGhost = {
  background: "transparent",
  color: BRAND.muted,
  border: `1px solid ${BRAND.line}`,
  borderRadius: "8px",
  padding: "0.5rem 0.85rem",
  fontSize: "0.85rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};

const primaryBtn = {
  background: BRAND.green,
  color: "white",
  border: "none",
  borderRadius: "8px",
  padding: "0.55rem 1.1rem",
  fontSize: "0.92rem",
  fontWeight: 800,
  fontFamily: "inherit",
  cursor: "pointer",
};
